import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button, Box, Tooltip } from '@mui/material';
import * as XLSX from 'xlsx-js-style';
import {
  getStoredCompanyDetails,
  getCompanyDisplayFromDetails,
  getShortCompanyName,
} from '../../services/companyService';

// ─── Annexure-C columns ───────────────────────────────────────────────────────
const COLUMNS = [
  { key: 'sr_no',                                    label: 'Sr.\nNo',                                             width: '60px',  align: 'center' },
  { key: 'name',                                     label: 'Name of the\nEmployee',                               width: '220px', align: 'left'   },
  { key: 'designation',                              label: 'Designation',                                         width: '200px', align: 'left'   },
  { key: 'remuneration',                             label: 'Remuneration\nReceived (₹)',                          width: '170px', align: 'right'  },
  { key: 'nature_of_employment',                     label: 'Nature of\nEmployment',                               width: '140px', align: 'center' },
  { key: 'qualifications_and_experience',            label: 'Qualifications\n& Experience',                       width: '220px', align: 'left'   },
  { key: 'date_of_commencement',                     label: 'Date of\nCommencement\nof Employment',               width: '160px', align: 'center' },
  { key: 'age',                                      label: 'Age',                                                 width: '65px',  align: 'center' },
  { key: 'last_employment',                          label: 'Last Employment\nHeld',                              width: '200px', align: 'left'   },
  { key: 'percentage_of_equity_shares_held',         label: '% of Equity\nShares Held',                          width: '140px', align: 'center' },
  { key: 'nature_of_relation_with_director_manager', label: 'Nature of Relation\nwith Director\nor Manager',      width: '220px', align: 'left'   },
];

// Fixed sticky columns: Sr. No + Name
const FIXED_COLS = 2;
const fixedColumnWidths = COLUMNS.slice(0, FIXED_COLS).map((c) => parseInt(c.width, 10));
const totalTableWidth   = COLUMNS.reduce((s, c) => s + parseInt(c.width, 10), 0);

const NUM_COLS = COLUMNS.length;

const EXCEL_HEADERS = [
  'Sr. No.',
  'Name of the Employee',
  'Designation',
  'Remuneration Received',
  'Nature of Employment',
  'Qualifications & Experience',
  'Date of Commencement of Employment',
  'Age',
  'Last Employment Held',
  'Percentage of Equity Shares Held',
  'Nature of Relation with Director or Manager',
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatNumber(num) {
  if (num === null || num === undefined || num === '') return '';
  return Number(num).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return `${String(d.getDate()).padStart(2,'0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
}

function getCellValue(emp, key, index) {
  switch (key) {
    case 'sr_no':                                    return emp.sr_no || (index + 1);
    case 'name':                                     return emp.name || '';
    case 'designation':                              return emp.designation || '';
    case 'remuneration':                             return formatNumber(emp.remuneration);
    case 'nature_of_employment':                     return emp.nature_of_employment || '';
    case 'qualifications_and_experience':            return emp.qualifications_and_experience || '';
    case 'date_of_commencement':                     return emp.date_of_commencement ? formatDate(emp.date_of_commencement) : '';
    case 'age':                                      return emp.age != null ? emp.age : '';
    case 'last_employment':                          return emp.last_employment || '';
    case 'percentage_of_equity_shares_held':         return emp.percentage_of_equity_shares_held || '';
    case 'nature_of_relation_with_director_manager': return emp.nature_of_relation_with_director_manager || '';
    default:                                         return '';
  }
}

function getRawCellValue(emp, key, index) {
  switch (key) {
    case 'sr_no':        return emp.sr_no || (index + 1);
    case 'remuneration': return emp.remuneration != null ? Number(emp.remuneration) : 0;
    default:             return getCellValue(emp, key, index);
  }
}

// sticky left offset for a column index
function getLeft(index) {
  if (index === 0) return 0;
  return fixedColumnWidths.slice(0, index).reduce((s, w) => s + w, 0);
}

// border helpers — matches GratuityReportGenerated exactly
const borderRightHeader = (index) => index === FIXED_COLS - 1 ? '2px solid #adb5bd' : '1px solid #3a3f46';
const borderRightBody   = (index) => index === FIXED_COLS - 1 ? '2px solid #dee2e6' : '1px solid #dee2e6';
const headerBarBg = '#0d0f12';

// ─── Word (HTML blob) export ──────────────────────────────────────────────────
function buildWordHtml({ companyName, cin, financialYear, data, totalRemuneration, place, today }) {
  const headerRow = COLUMNS.map((c) =>
    `<th style="border:1px solid #000;padding:4px 3px;font-size:8pt;text-align:center;vertical-align:middle;word-wrap:break-word;">${c.label.replace(/\n/g, '<br/>')}</th>`
  ).join('');

  const dataRows = data.map((emp, i) => {
    const cells = COLUMNS.map((c) => {
      const val   = getCellValue(emp, c.key, i);
      const align = c.align === 'right' ? 'right' : c.align === 'center' ? 'center' : 'left';
      return `<td style="border:1px solid #000;padding:4px 3px;font-size:8pt;text-align:${align};vertical-align:middle;">${val !== '' ? val : '&nbsp;'}</td>`;
    }).join('');
    return `<tr>${cells}</tr>`;
  });

  const emptyRows = Array.from({ length: Math.max(0, 10 - data.length) }, (_, i) => {
    const srNo  = data.length + i + 1;
    const cells = COLUMNS.map((c) =>
      `<td style="border:1px solid #000;padding:4px 3px;font-size:8pt;">${c.key === 'sr_no' ? srNo : '&nbsp;'}</td>`
    ).join('');
    return `<tr>${cells}</tr>`;
  });

  // Grand total row
  const totalCells = COLUMNS.map((c) => {
    let val   = '';
    let align = c.align === 'right' ? 'right' : c.align === 'center' ? 'center' : 'left';
    if (c.key === 'name')        { val = 'GRAND TOTAL'; align = 'left'; }
    if (c.key === 'remuneration') { val = formatNumber(totalRemuneration); align = 'right'; }
    return `<td style="border:1px solid #000;padding:4px 3px;font-size:8pt;text-align:${align};vertical-align:middle;font-weight:bold;background:#f8f9fa;">${val || '&nbsp;'}</td>`;
  }).join('');
  const totalRow = `<tr>${totalCells}</tr>`;

  const cinLine = cin ? `<div style="text-align:center;font-size:10pt;">${cin}</div>` : '';

  return `
<html xmlns:o='urn:schemas-microsoft-com:office:office'
      xmlns:w='urn:schemas-microsoft-com:office:word'
      xmlns='http://www.w3.org/TR/REC-html40'>
<head>
  <meta charset='utf-8'>
  <title>Annexure C - Top Ten Employees</title>
  <!--[if gte mso 9]>
  <xml>
    <w:WordDocument>
      <w:View>Print</w:View>
      <w:Zoom>90</w:Zoom>
      <w:DoNotOptimizeForBrowser/>
    </w:WordDocument>
  </xml>
  <![endif]-->
  <style>
    @page WordSection1 {
      size: 841.9pt 595.3pt;
      mso-page-orientation: landscape;
      margin: 1.5cm 1cm;
    }
    @page { size: A4 landscape; margin: 1.5cm 1cm; }
    div.WordSection1 { page: WordSection1; }
    body { font-family: Arial, sans-serif; font-size: 10pt; margin: 0; }
    table { border-collapse: collapse; width: 100%; table-layout: fixed; }
    .company-name { text-align: center; font-size: 12pt; font-weight: bold; margin-bottom: 2px; }
    hr { border: none; border-top: 1px solid #000; margin: 6px 0; }
    .annexure-title { text-align: center; font-weight: bold; text-decoration: underline; font-size: 11pt; margin: 8px 0 4px 0; }
    .report-title { text-align: center; font-weight: bold; font-size: 10pt; margin-bottom: 10px; }
    .footer-line { margin-top: 16px; font-size: 10pt; }
  </style>
</head>
<body>
<div class="WordSection1">
  <div class="company-name">${companyName || ''}</div>
  ${cinLine}
  <hr/>
  <div class="annexure-title">ANNEXURE &ndash;C</div>
  <div class="report-title">DISCLOSURE OF THE TOP TEN EMPLOYEES OF THE COMPANY for FY ${financialYear}</div>
  <table>
    <thead>
      <tr style="background-color:#f2f2f2;">${headerRow}</tr>
    </thead>
    <tbody>
      ${dataRows.join('\n      ')}
      ${emptyRows.join('\n      ')}
      ${totalRow}
    </tbody>
  </table>
  <div class="footer-line">Date &nbsp;: &nbsp;${today}</div>
  <div class="footer-line">Place : &nbsp;${place || ''}</div>
</div>
</body>
</html>`.trim();
}

// ─── Main component ───────────────────────────────────────────────────────────
function TopEmployeesReportGenerated() {
  const location = useLocation();
  const companyDetails =
    useSelector((state) => state.auth.activeCompanyDetails) ||
    getStoredCompanyDetails();
  const { companyName, locality, addressLine1, addressLine2 } = getCompanyDisplayFromDetails(companyDetails || {}); 
  const fullPlace = [addressLine2].filter(Boolean).join(', ');
  const cin = companyDetails?.cin || companyDetails?.company_cin || '';

  const reportData    = location.state?.reportData || {};
  const financialYear = reportData.financial_year || location.state?.financialYear || '';
  const data          = Array.isArray(reportData.data) ? reportData.data : [];

  const totalRemuneration = data.reduce((sum, emp) => sum + Number(emp.remuneration || 0), 0);

  const today = new Date().toLocaleDateString('en-IN', {
    day: '2-digit', month: 'long', year: 'numeric',
  });

  // ── Excel export ─────────────────────────────────────────────────────────
  const handleExcelDownload = () => {
    const wb      = XLSX.utils.book_new();
    const wsData  = [];

    wsData.push([companyName || '', ...Array(NUM_COLS - 1).fill('')]);
    wsData.push([cin || '',         ...Array(NUM_COLS - 1).fill('')]);
    wsData.push([`Top 10 Employees by Salary – FY ${financialYear}`, ...Array(NUM_COLS - 1).fill('')]);
    wsData.push([`ANNEXURE –C  |  DISCLOSURE OF THE TOP TEN EMPLOYEES OF THE COMPANY for FY ${financialYear}`, ...Array(NUM_COLS - 1).fill('')]);
    wsData.push(EXCEL_HEADERS);

    data.forEach((emp, i) => wsData.push(COLUMNS.map((c) => getRawCellValue(emp, c.key, i))));
    for (let e = data.length; e < 10; e++) {
      wsData.push(COLUMNS.map((c) => (c.key === 'sr_no' ? e + 1 : '')));
    }
    // Grand total row (row 15)
    wsData.push(COLUMNS.map((c) => {
      if (c.key === 'name')        return 'GRAND TOTAL';
      if (c.key === 'remuneration') return totalRemuneration;
      return '';
    }));
    // Blank + footer (rows 16-18)
    wsData.push(Array(NUM_COLS).fill(''));
    wsData.push([`Date  :  ${today}`, ...Array(NUM_COLS - 1).fill('')]);
    wsData.push([`Place :  ${fullPlace || ''}`, ...Array(NUM_COLS - 1).fill('')]);

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    ws['!cols'] = [
      { wch: 6 }, { wch: 24 }, { wch: 20 }, { wch: 18 }, { wch: 14 },
      { wch: 22 }, { wch: 20 }, { wch: 6 }, { wch: 18 }, { wch: 14 }, { wch: 24 },
    ];
    ws['!rows'] = [];
    ws['!rows'][4] = { hpt: 44 };

    const footerBlankRow = 16;
    const footerDateRow  = 17;
    const footerPlaceRow = 18;
    const grandTotalRow  = 15;

    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: NUM_COLS - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: NUM_COLS - 1 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: NUM_COLS - 1 } },
      { s: { r: 3, c: 0 }, e: { r: 3, c: NUM_COLS - 1 } },
      { s: { r: footerBlankRow, c: 0 }, e: { r: footerBlankRow, c: NUM_COLS - 1 } },
      { s: { r: footerDateRow,  c: 0 }, e: { r: footerDateRow,  c: NUM_COLS - 1 } },
      { s: { r: footerPlaceRow, c: 0 }, e: { r: footerPlaceRow, c: NUM_COLS - 1 } },
    ];

    const border = {
      top:    { style: 'thin', color: { rgb: '000000' } },
      bottom: { style: 'thin', color: { rgb: '000000' } },
      left:   { style: 'thin', color: { rgb: '000000' } },
      right:  { style: 'thin', color: { rgb: '000000' } },
    };
    const companyStyle  = { font: { bold: true, sz: 14 }, alignment: { horizontal: 'center', vertical: 'center' }, border };
    const annexureStyle = { font: { bold: true, sz: 11, underline: true }, alignment: { horizontal: 'center', vertical: 'center', wrapText: true }, border };
    const colHeaderStyle = {
      font:      { bold: true, color: { rgb: 'F1F3F5' } },
      fill:      { fgColor: { rgb: '0D0F12' } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border,
    };
    const dataStyle  = { border, alignment: { vertical: 'center', wrapText: true } };
    const totalStyle = { font: { bold: true }, fill: { fgColor: { rgb: 'F8F9FA' } }, border, alignment: { vertical: 'center' } };
    const footerStyle = { font: { bold: true, sz: 11 }, alignment: { horizontal: 'left', vertical: 'center' } };

    // Header rows 0-3
    [companyStyle, companyStyle, companyStyle, annexureStyle].forEach((style, row) => {
      const addr = XLSX.utils.encode_cell({ r: row, c: 0 });
      if (ws[addr]) ws[addr].s = style;
      for (let col = 1; col < NUM_COLS; col++) {
        const a = XLSX.utils.encode_cell({ r: row, c: col });
        if (ws[a]) delete ws[a];
      }
    });
    // Column header row (row 4)
    for (let col = 0; col < NUM_COLS; col++) {
      const addr = XLSX.utils.encode_cell({ r: 4, c: col });
      if (ws[addr]) ws[addr].s = colHeaderStyle;
    }
    // Data rows (5-14)
    for (let row = 5; row <= 14; row++) {
      for (let col = 0; col < NUM_COLS; col++) {
        const addr = XLSX.utils.encode_cell({ r: row, c: col });
        if (ws[addr]) {
          const colDef = COLUMNS[col];
          const h = colDef?.align === 'right' ? 'right' : colDef?.align === 'center' ? 'center' : 'left';
          ws[addr].s = { ...dataStyle, alignment: { ...dataStyle.alignment, horizontal: h } };
          if (col === 3) ws[addr].z = '#,##0.00';
        }
      }
    }
    // Grand total row (row 15)
    for (let col = 0; col < NUM_COLS; col++) {
      const addr = XLSX.utils.encode_cell({ r: grandTotalRow, c: col });
      if (ws[addr]) {
        const colDef = COLUMNS[col];
        const h = col === 3 ? 'right' : col === 1 ? 'left' : (colDef?.align || 'left');
        ws[addr].s = { ...totalStyle, alignment: { ...totalStyle.alignment, horizontal: h } };
        if (col === 3) ws[addr].z = '#,##0.00';
      }
    }
    // Footer rows
    [footerDateRow, footerPlaceRow].forEach((row) => {
      const addr = XLSX.utils.encode_cell({ r: row, c: 0 });
      if (ws[addr]) ws[addr].s = footerStyle;
      for (let col = 1; col < NUM_COLS; col++) {
        const a = XLSX.utils.encode_cell({ r: row, c: col });
        if (ws[a]) delete ws[a];
      }
    });
    for (let col = 0; col < NUM_COLS; col++) {
      const a = XLSX.utils.encode_cell({ r: footerBlankRow, c: col });
      if (ws[a]) delete ws[a];
    }

    XLSX.utils.book_append_sheet(wb, ws, 'Annexure C');
    XLSX.writeFile(wb, `${getShortCompanyName(companyName) || 'Company'} Top Employees FY ${financialYear}.xlsx`);
  };

  // ── Word export ───────────────────────────────────────────────────────────
  const handleWordDownload = () => {
    const html = buildWordHtml({ companyName, cin, financialYear, data, totalRemuneration, place: fullPlace, today });
    const blob = new Blob(['\ufeff', html], { type: 'application/msword;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${getShortCompanyName(companyName) || 'Company'} Annexure C FY ${financialYear}.doc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ─── Grand-total cell value ───────────────────────────────────────────────
  const getGrandTotalCell = (key) => {
    if (key === 'name')        return 'GRAND TOTAL';
    if (key === 'remuneration') return formatNumber(totalRemuneration);
    return '';
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        padding: '10px',
        overflowX: 'hidden',
        maxWidth: '100vw',
      }}
    >
      <Box sx={{ width: '100%', maxWidth: '100%', padding: '20px', minWidth: 0, overflow: 'hidden' }}>

        {/* ── Download buttons ── */}
        <Box sx={{ display: 'flex', gap: 2, marginBottom: 2, flexWrap: 'wrap' }}>
          <Button variant="contained" color="success" onClick={handleExcelDownload}>
            Download Excel (Annexure C)
          </Button>
          <Button variant="contained" color="primary" onClick={handleWordDownload}>
            Download Word (Annexure C)
          </Button>
        </Box>

        {/* ── Report title strip ── */}
        <Box
          sx={{
            textAlign: 'center',
            padding: '12px 16px',
            marginBottom: '12px',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            backgroundColor: '#fff',
          }}
        >
          {companyName && (
            <div style={{ fontWeight: 'bold', fontSize: '15px' }}>{companyName}</div>
          )}
          {cin && <div style={{ fontSize: '13px', color: '#555' }}>{cin}</div>}
          <hr style={{ margin: '6px 0', borderColor: '#000' }} />
          <div style={{ fontWeight: 'bold', fontSize: '13px', textDecoration: 'underline', marginBottom: '4px' }}>
            ANNEXURE – C
          </div>
          <div style={{ fontWeight: 'bold', fontSize: '12px' }}>
            DISCLOSURE OF THE TOP TEN EMPLOYEES OF THE COMPANY for FY {financialYear}
          </div>
        </Box>

        {/* ── Table ── */}
        {data.length === 0 ? (
          <Box sx={{ textAlign: 'center', padding: 4, border: '1px solid #dee2e6', borderRadius: '4px' }}>
            No employees found for the selected financial year.
          </Box>
        ) : (
          <div
            style={{
              height: '600px',
              width: '100%',
              maxWidth: '100%',
              overflow: 'auto',
              overflowX: 'auto',
              overflowY: 'auto',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              backgroundColor: '#fff',
              isolation: 'isolate',
            }}
          >
            <table
              style={{
                display: 'table',
                overflow: 'visible',
                whiteSpace: 'normal',
                width: `${totalTableWidth}px`,
                minWidth: `${totalTableWidth}px`,
                tableLayout: 'fixed',
                borderCollapse: 'collapse',
                margin: 0,
              }}
              className="table"
            >
              {/* ── Header ── */}
              <thead
                style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  background: '#212529',
                  color: 'white',
                  boxShadow: '0 2px 2px -1px rgba(0,0,0,0.2)',
                }}
              >
                <tr>
                  {COLUMNS.map((col, index) => {
                    const isFixed = index < FIXED_COLS;
                    const left    = isFixed ? getLeft(index) : undefined;
                    return (
                      <th
                        key={col.key}
                        style={{
                          width:          col.width,
                          minWidth:       col.width,
                          padding:        '12px 8px',
                          boxSizing:      'border-box',
                          textAlign:      col.align,
                          verticalAlign:  'middle',
                          whiteSpace:     'pre-line',
                          lineHeight:     1.25,
                          borderTop:      'none',
                          borderLeft:     'none',
                          borderBottom:   'none',
                          borderRight:    borderRightHeader(index),
                          background:     headerBarBg,
                          ...(isFixed && { position: 'sticky', left: `${left}px`, zIndex: 11 }),
                        }}
                      >
                        <Tooltip title={col.label.replace(/\n/g, ' ')} placement="top" arrow>
                          <div style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                            {col.label}
                          </div>
                        </Tooltip>
                      </th>
                    );
                  })}
                </tr>
              </thead>

              {/* ── Body ── */}
              <tbody>
                {data.map((emp, i) => (
                  <tr key={emp.employee_id || i} style={{ height: '48px', backgroundColor: 'white' }}>
                    {COLUMNS.map((col, index) => {
                      const isFixed = index < FIXED_COLS;
                      const left    = isFixed ? getLeft(index) : undefined;
                      const value   = getCellValue(emp, col.key, i);
                      return (
                        <td
                          key={col.key}
                          title={getCellValue(emp, col.key, i) !== '' ? String(getCellValue(emp, col.key, i)) : undefined}
                          style={{
                            width:         col.width,
                            minWidth:      col.width,
                            padding:       '12px 8px',
                            boxSizing:     'border-box',
                            textAlign:     col.align,
                            overflow:      'hidden',
                            textOverflow:  'ellipsis',
                            whiteSpace:    'nowrap',
                            borderBottom:  '1px solid #dee2e6',
                            borderRight:   borderRightBody(index),
                            ...(isFixed && { position: 'sticky', left: `${left}px`, zIndex: 5, background: 'white' }),
                          }}
                        >
                          {value !== '' && value != null ? value : <span style={{ color: '#adb5bd' }}>–</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}

                {/* ── Grand Total row ── */}
                <tr style={{ height: '48px', backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                  {COLUMNS.map((col, index) => {
                    const isFixed = index < FIXED_COLS;
                    const left    = isFixed ? getLeft(index) : undefined;
                    const value   = getGrandTotalCell(col.key);
                    const align   = col.key === 'name' ? 'left' : col.align;
                    return (
                      <td
                        key={col.key}
                        style={{
                          width:        col.width,
                          minWidth:     col.width,
                          padding:      '12px 8px',
                          boxSizing:    'border-box',
                          textAlign:    align,
                          overflow:     'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace:   'nowrap',
                          borderBottom: '1px solid #dee2e6',
                          borderRight:  borderRightBody(index),
                          ...(isFixed && { position: 'sticky', left: `${left}px`, zIndex: 5, background: '#f8f9fa' }),
                        }}
                      >
                        {value}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* ── Footer ── */}
        <Box
          sx={{
            marginTop: '16px',
            padding: '10px 16px',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            backgroundColor: '#f8f9fa',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            fontSize: '13px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontWeight: 'bold', minWidth: '40px' }}>Date</span>
            <span style={{ fontWeight: 'bold' }}>:</span>
            <span>{today}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontWeight: 'bold', minWidth: '40px' }}>Place</span>
            <span style={{ fontWeight: 'bold' }}>:</span>
            <span>{fullPlace || ''}</span>
          </div>
        </Box>

      </Box>
    </Box>
  );
}

export default TopEmployeesReportGenerated;
